import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ListChecks, Loader2 } from 'lucide-react';
import { Button, Card, CardContent, CardHeader } from '@/components/ui';

export interface SetupChecklistTask {
  id: string;
  title: string;
  description: string;
  href: string;
  done: boolean;
  /** Destination page tour that auto-starts on first visit (plain `href` navigation). */
  pageTourKey?: string | null;
}

export interface AcademyOwnerSetupChecklistProps {
  tasks: SetupChecklistTask[];
  onDismiss: () => void | Promise<void>;
  dismissing?: boolean;
  /** Anchor for guided tour highlight (e.g. `TOUR_STEP_IDS.SETUP_CHECKLIST`) */
  tourAnchorId?: string;
}

export function AcademyOwnerSetupChecklist({
  tasks,
  onDismiss,
  dismissing = false,
  tourAnchorId,
}: AcademyOwnerSetupChecklistProps) {
  const doneCount = tasks.filter((t) => t.done).length;
  const total = tasks.length;

  return (
    <Card
      id={tourAnchorId}
      className="mb-4 border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-900/50 dark:bg-emerald-950/20"
    >
      <CardHeader className="space-y-0 p-3 pb-2 sm:p-4 sm:pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
              <ListChecks className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-400" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold leading-tight text-gray-900 dark:text-white">
                Getting started
                <span className="ml-1.5 font-normal text-emerald-800 dark:text-emerald-300">
                  ({doneCount}/{total})
                </span>
              </h2>
              <p className="text-xs leading-snug text-gray-500 dark:text-gray-400">
                Finish setup when you have time — links open the right screen.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            onClick={() => void onDismiss()}
            disabled={dismissing}
            aria-label="Hide getting started checklist"
          >
            {dismissing ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
                …
              </>
            ) : (
              'Hide'
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0 sm:p-4 sm:pt-0">
        <ul className="divide-y divide-emerald-200/60 dark:divide-emerald-900/40 rounded-md border border-emerald-200/60 dark:border-emerald-900/40 bg-white/80 text-sm dark:bg-gray-900/40">
          {tasks.map((task) => (
            <li key={task.id} className="flex gap-2 px-2.5 py-2 first:rounded-t-md last:rounded-b-md sm:px-3">
              <span className="mt-0.5 shrink-0" aria-hidden>
                {task.done ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <Circle className="h-4 w-4 text-gray-300 dark:text-gray-600" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                  <span
                    className={`text-sm font-medium leading-tight ${
                      task.done
                        ? 'text-gray-500 line-through dark:text-gray-500'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {task.title}
                  </span>
                  {!task.done && (
                    <Link
                      to={task.href}
                      className="text-xs font-medium text-emerald-700 hover:text-emerald-800 underline-offset-2 hover:underline dark:text-emerald-400 dark:hover:text-emerald-300"
                    >
                      Open
                    </Link>
                  )}
                </div>
                <p className="mt-0.5 text-xs leading-snug text-gray-600 dark:text-gray-400">{task.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
